/**
 * Nutrition analysis service — text, photo, and combined input.
 *
 * Text path:   kind = 'nutrition_text'     → backend AI returns per-100g macros
 * Photo path:  kind = 'nutrition_image'    → backend OCR extracts label macros
 * Combined:    kind = 'nutrition_combined' → backend uses both inputs
 *
 * All paths go through the same function (analyzeNutrition) and share:
 *   - Response shape validation (fail-loud on unexpected backend contracts)
 *   - Macro plausibility + calorie reconciliation gate
 *   - Confirmation-required contract for suspicious results
 *   - Retry policy (one retry on transient failures only)
 *
 * FE HARDENING (v2):
 *   - Circuit breaker: after repeated failures, route to deterministic fallback.
 *   - Differentiated timeouts: text 8s, image/combined 15s.
 *   - Hard schema discard: shape assertion failure → throw (no permissive casting).
 *   - extractItems indirection removed (callers use splitMealItems directly).
 */

import { env } from '../../utils/env';
import type { MacroBreakdown } from '../../types/nutrition';
import { getCacheKey, getCachedResult, setCachedResult } from '../../utils/nutritionCache';
import { reportAIError } from '../errorReporting.service';
import { loggedFetch } from '../../utils/apiLogger';
import { BudgetExceededError } from '../../types/errors';

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

export interface NutritionExtraction {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fibre: number;
  creatine?: number;
  hydration_ml?: number;
  needs_amount?: boolean;
  needs_manual_macros?: boolean;
  confidence: 'high' | 'medium' | 'low';
  method: 'extracted' | 'estimated';
  uses_per_portion?: boolean;
  notes?: string;
  /** True when the UI must show a review screen before saving. */
  requiresConfirmation: boolean;
  /** Human-readable reasons why confirmation is required (empty if not required). */
  suspicionReasons: string[];
  /** Raw backend items — present only when requiresConfirmation is true. Used by ConfirmationSheet. */
  confirmationItems?: ConfirmationItem[];
  /** Label weight metadata — extracted from food label, only present for label uploads */
  serving_size_grams?: number | null;
  net_weight_grams?: number | null;
  servings_per_container?: number | null;
  label_volume_ml?: number | null;
}

/** Item shape passed to ConfirmationSheet and submitted back via nutrition_confirmed. */
export interface ConfirmationItem {
  name: string;
  quantity: number | null;
  unit: string | null;
  calories: number | null;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
  fibre: number | null;
  hydration_ml: number | null;
  creatine: number | null;
  needs_amount: boolean;
  missing_fields: string[] | null;
  confidence?: string;
  notes?: string;
}

export interface ItemExtraction {
  items: string[];
}

export interface NutritionAnalysisRequest {
  imageUrl?: string;
  imageBase64?: string;
  textDescription?: string;
  userId: string;
  userProvidedMacros?: Partial<MacroBreakdown>;
  useHighQualityModel?: boolean;
  isRestaurant?: boolean;
}

// ────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────

/** Differentiated timeouts by input type */
const TIMEOUT_TEXT_MS = 8_000;
const TIMEOUT_IMAGE_MS = 15_000;

const RETRY_MIN_MS = 1_200;
const RETRY_MAX_MS = 2_500;

/** Status codes that are safe to retry (transient failures only). */
const RETRYABLE_STATUSES = new Set([408, 500, 502, 503, 504]);

/** Status codes that must never be retried. */
const NON_RETRYABLE_STATUSES = new Set([400, 401, 402, 403, 404, 422, 429]);

/** Calorie mismatch thresholds for reconciliation. */
const MISMATCH_OK_PCT = 0.15;
const MISMATCH_OK_ABS = 60;
const MISMATCH_SUSPICIOUS_PCT = 0.35;
const MISMATCH_SUSPICIOUS_ABS = 150;

/** Absolute sanity bounds (per serving / per item). */
const MAX_CALORIES_PER_SERVING = 2_000;
const MAX_MACRO_GRAMS = 200;

// ────────────────────────────────────────────────────────────────
// Circuit Breaker
// ────────────────────────────────────────────────────────────────

const BREAKER_THRESHOLD = 3;
const BREAKER_WINDOW_MS = 2 * 60 * 1000;    // 2 minutes
const BREAKER_COOLDOWN_MS = 3 * 60 * 1000;  // 3 minutes

interface CircuitBreakerState {
  failures: number[];
  trippedAt: number | null;
}

const circuitBreaker: CircuitBreakerState = {
  failures: [],
  trippedAt: null,
};

function isCircuitOpen(): boolean {
  if (circuitBreaker.trippedAt) {
    if (Date.now() - circuitBreaker.trippedAt > BREAKER_COOLDOWN_MS) {
      circuitBreaker.trippedAt = null;
      circuitBreaker.failures = [];
      console.log('[NutritionService] Circuit breaker reset — AI calls re-enabled');
      return false;
    }
    return true;
  }
  return false;
}

function recordFailure(): void {
  const now = Date.now();
  circuitBreaker.failures.push(now);
  circuitBreaker.failures = circuitBreaker.failures.filter(ts => now - ts < BREAKER_WINDOW_MS);
  if (circuitBreaker.failures.length >= BREAKER_THRESHOLD) {
    circuitBreaker.trippedAt = now;
    console.warn('[NutritionService] Circuit breaker TRIPPED — deterministic-only mode for', BREAKER_COOLDOWN_MS / 1000, 's');
  }
}

function recordSuccess(): void {
  circuitBreaker.failures = [];
}

/** Exported for parseItem to check before making AI calls */
export function isNutritionCircuitOpen(): boolean {
  return isCircuitOpen();
}

// ────────────────────────────────────────────────────────────────
// Safe coercion
// ────────────────────────────────────────────────────────────────

function safeNumber(v: unknown): number | null {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function safeNumberOrZero(v: unknown, fieldName: string, reasons: string[]): number {
  const n = safeNumber(v);
  if (n === null) {
    reasons.push(`${fieldName} had an unexpected value and was set to 0`);
    return 0;
  }
  return n;
}

// ────────────────────────────────────────────────────────────────
// Image input normalisation
// ────────────────────────────────────────────────────────────────

interface NormalisedImageInput {
  imageBase64?: string;
  imageUrl?: string;
}

function normaliseImageInput(
  imageBase64?: string,
  imageUrl?: string,
): NormalisedImageInput {
  if (imageBase64 && imageUrl) {
    console.warn('[NutritionService] Both imageBase64 and imageUrl provided; using imageBase64');
    return { imageBase64 };
  }
  if (imageBase64) return { imageBase64 };
  if (imageUrl) return { imageUrl };
  return {};
}

// ────────────────────────────────────────────────────────────────
// Guards & Helpers
// ────────────────────────────────────────────────────────────────

function isDemoMode(): boolean {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('demo') === '1';
}

const WATER_PATTERN = /^(water|a bit of water|some water|drink water|had water)$/i;

function isPlainWater(text: string): boolean {
  return WATER_PATTERN.test(text.trim());
}

/**
 * Runtime shape guard for backend nutrition responses.
 * HARD DISCARD: throws on any structural violation. No permissive fallback.
 */
function assertResponseShape(result: unknown): asserts result is {
  total?: Record<string, unknown>;
  items?: unknown[];
  confidence?: string;
  method?: string;
  needs_amount?: boolean;
  needs_manual_macros?: boolean;
  uses_per_portion?: boolean;
  notes?: string;
} {
  if (result === null || typeof result !== 'object') {
    throw new NutritionShapeError('Backend returned null or non-object result');
  }

  const r = result as Record<string, unknown>;

  if (r.total !== undefined && r.total !== null) {
    if (typeof r.total !== 'object') {
      throw new NutritionShapeError('result.total is not an object');
    }
    const t = r.total as Record<string, unknown>;
    for (const field of ['calories', 'protein', 'carbs', 'fat']) {
      if (t[field] !== undefined && t[field] !== null && typeof t[field] !== 'number') {
        throw new NutritionShapeError(`result.total.${field} is ${typeof t[field]}, expected number or null`);
      }
    }
  }

  if (r.items !== undefined && r.items !== null) {
    if (!Array.isArray(r.items)) {
      throw new NutritionShapeError('result.items is not an array');
    }
    for (let i = 0; i < r.items.length; i++) {
      if (r.items[i] === null || typeof r.items[i] !== 'object') {
        throw new NutritionShapeError(`result.items[${i}] is not an object`);
      }
    }
  }

  if (r.confidence !== undefined && !['high', 'medium', 'low'].includes(r.confidence as string)) {
    throw new NutritionShapeError(`result.confidence is "${r.confidence}", expected high|medium|low`);
  }
}

class NutritionShapeError extends Error {
  public readonly code = 'NUTRITION_OCR_BAD_RESPONSE_SHAPE';
  constructor(detail: string) {
    super(`Unexpected backend response shape: ${detail}`);
    this.name = 'NutritionShapeError';
  }
}

// ────────────────────────────────────────────────────────────────
// Plausibility & Reconciliation
// ────────────────────────────────────────────────────────────────

interface PlausibilityResult {
  requiresConfirmation: boolean;
  suspicionReasons: string[];
}

function checkPlausibility(
  calories: number,
  protein: number,
  carbs: number,
  fat: number,
  fibre: number,
): PlausibilityResult {
  const reasons: string[] = [];

  if (calories < 0 || protein < 0 || carbs < 0 || fat < 0 || fibre < 0) {
    reasons.push('Negative values detected — please check the numbers');
    return { requiresConfirmation: true, suspicionReasons: reasons };
  }

  if (calories > MAX_CALORIES_PER_SERVING) {
    reasons.push(`Calories (${Math.round(calories)}) seem very high for a single item`);
  }
  if (protein > MAX_MACRO_GRAMS) {
    reasons.push(`Protein (${Math.round(protein)}g) seems unusually high`);
  }
  if (carbs > MAX_MACRO_GRAMS) {
    reasons.push(`Carbs (${Math.round(carbs)}g) seems unusually high`);
  }
  if (fat > MAX_MACRO_GRAMS) {
    reasons.push(`Fat (${Math.round(fat)}g) seems unusually high`);
  }

  if (calories > 0 && (protein > 0 || carbs > 0 || fat > 0)) {
    const derivedA = protein * 4 + carbs * 4 + fat * 9;
    let derivedB = derivedA;
    if (fibre > 0 && fibre <= carbs) {
      derivedB = protein * 4 + (carbs - fibre) * 4 + fat * 9;
    }

    const diffA = Math.abs(calories - derivedA);
    const diffB = Math.abs(calories - derivedB);
    const bestDerived = diffA <= diffB ? derivedA : derivedB;
    const diff = Math.min(diffA, diffB);
    const pct = calories > 0 ? diff / calories : 0;

    if (pct > MISMATCH_SUSPICIOUS_PCT || diff > MISMATCH_SUSPICIOUS_ABS) {
      reasons.push(
        `Calories (${Math.round(calories)}) don't match macros (expected ~${Math.round(bestDerived)}) — ${Math.round(pct * 100)}% difference`,
      );
    } else if (pct > MISMATCH_OK_PCT || diff > MISMATCH_OK_ABS) {
      reasons.push(
        `Calories (${Math.round(calories)}) are slightly off from macros (expected ~${Math.round(bestDerived)})`,
      );
    }
  }

  if (calories === 0 && (protein > 0 || carbs > 0 || fat > 0)) {
    reasons.push('Calories are zero but macros are not — please verify the label');
  }

  return {
    requiresConfirmation: reasons.length > 0,
    suspicionReasons: reasons,
  };
}

// ────────────────────────────────────────────────────────────────
// Retry-aware fetch
// ────────────────────────────────────────────────────────────────

function jitteredDelay(): number {
  return RETRY_MIN_MS + Math.random() * (RETRY_MAX_MS - RETRY_MIN_MS);
}

async function fetchWithRetry(
  url: string,
  init: Omit<RequestInit, 'signal'>,
  timeoutMs: number,
): Promise<Response> {
  const attempt = async (): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await loggedFetch(url, { ...init, signal: controller.signal });
      return response;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  try {
    const response = await attempt();

    if (!response.ok && NON_RETRYABLE_STATUSES.has(response.status)) {
      return response;
    }

    if (!response.ok && RETRYABLE_STATUSES.has(response.status)) {
      await new Promise((r) => setTimeout(r, jitteredDelay()));
      return attempt();
    }

    return response;
  } catch (err: unknown) {
    await new Promise((r) => setTimeout(r, jitteredDelay()));
    return attempt();
  }
}

// ────────────────────────────────────────────────────────────────
// Response normalisation
// ────────────────────────────────────────────────────────────────

interface NormalisedTotals {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fibre: number;
  creatine: number;
  hydration_ml: number;
}

function normaliseTotals(backendResult: Record<string, unknown>, coercionReasons: string[]): NormalisedTotals {
  const zero: NormalisedTotals = { calories: 0, protein: 0, carbs: 0, fat: 0, fibre: 0, creatine: 0, hydration_ml: 0 };

  let source: Record<string, unknown> | null = (backendResult.total as Record<string, unknown>) || null;

  if (source && source.calories === null && source.protein === null && source.carbs === null) {
    source = null;
  }

  if (!source && Array.isArray(backendResult.items) && backendResult.items.length > 0) {
    const summed = { ...zero };
    for (const item of backendResult.items) {
      const src = (item as Record<string, unknown>).total ||
                  (item as Record<string, unknown>).macros ||
                  (item as Record<string, unknown>);
      const s = src as Record<string, unknown>;
      summed.calories += safeNumberOrZero(s.calories, 'item.calories', coercionReasons);
      summed.protein += safeNumberOrZero(s.protein, 'item.protein', coercionReasons);
      summed.carbs += safeNumberOrZero(s.carbs, 'item.carbs', coercionReasons);
      summed.fat += safeNumberOrZero(s.fat, 'item.fat', coercionReasons);
      summed.fibre += safeNumberOrZero(s.fibre, 'item.fibre', coercionReasons);
      summed.creatine += safeNumberOrZero(s.creatine, 'item.creatine', coercionReasons);
      summed.hydration_ml += safeNumberOrZero(s.hydration_ml, 'item.hydration_ml', coercionReasons);
    }
    return summed;
  }

  if (!source) {
    source = backendResult;
  }

  const s = source as Record<string, unknown>;
  return {
    calories: safeNumberOrZero(s.calories, 'calories', coercionReasons),
    protein: safeNumberOrZero(s.protein, 'protein', coercionReasons),
    carbs: safeNumberOrZero(s.carbs, 'carbs', coercionReasons),
    fat: safeNumberOrZero(s.fat, 'fat', coercionReasons),
    fibre: safeNumberOrZero(s.fibre, 'fibre', coercionReasons),
    creatine: safeNumberOrZero(s.creatine, 'creatine', coercionReasons),
    hydration_ml: safeNumberOrZero(s.hydration_ml, 'hydration_ml', coercionReasons),
  };
}

// ────────────────────────────────────────────────────────────────
// Image pre-check
// ────────────────────────────────────────────────────────────────

export interface ImagePreCheckResult {
  ok: boolean;
  warning?: string;
}

export async function preCheckImage(file: File): Promise<ImagePreCheckResult> {
  if (file.size < 50_000) {
    return {
      ok: false,
      warning: 'This image is very small and may be unreadable. Try taking a closer photo of the label.',
    };
  }

  try {
    const dimensions = await getImageDimensions(file);
    const shortEdge = Math.min(dimensions.width, dimensions.height);
    if (shortEdge < 600) {
      return {
        ok: false,
        warning: 'This image is low resolution and the label text may be unreadable. Try a higher quality photo.',
      };
    }
  } catch {
    // Can't read dimensions — proceed anyway
  }

  return { ok: true };
}

function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to read image dimensions'));
    };
    img.src = url;
  });
}

// ────────────────────────────────────────────────────────────────
// Structured logging
// ────────────────────────────────────────────────────────────────

interface ParseLogEntry {
  event: 'nutrition_parse_result';
  inputType: 'text' | 'image' | 'combined';
  confidence: string;
  requiresConfirmation: boolean;
  suspicionCount: number;
  durationMs: number;
  circuitBreakerOpen?: boolean;
  error?: string;
}

function emitTelemetry(entry: ParseLogEntry): void {
  console.info('[NutritionOCR:telemetry]', JSON.stringify(entry));
}

// ────────────────────────────────────────────────────────────────
// Main: analyzeNutrition
// ────────────────────────────────────────────────────────────────

export async function analyzeNutrition(
  request: NutritionAnalysisRequest,
): Promise<NutritionExtraction> {
  const startTime = Date.now();

  // ── Guards ──
  if (isDemoMode()) {
    throw new Error('Demo mode: AI calls disabled. This is a read-only demo.');
  }
  if (!navigator.onLine) {
    throw new Error('AI analysis requires an internet connection. Please connect to the internet and try again.');
  }

  // ── Circuit breaker check ──
  if (isCircuitOpen()) {
    throw new Error('AI service temporarily unavailable. Please try again in a few minutes or enter macros manually.');
  }

  let authToken: string;
  try {
    const { getFirebaseIdToken } = await import('../../utils/firebaseToken');
    authToken = await getFirebaseIdToken();
  } catch {
    throw new Error('Authentication required for nutrition analysis');
  }

  const { imageBase64, imageUrl, textDescription, userProvidedMacros, useHighQualityModel, userId } = request;

  const imageInput = normaliseImageInput(imageBase64, imageUrl);
  const hasImage = !!(imageInput.imageBase64 || imageInput.imageUrl);

  // ── Cache check (text-only) ──
  if (textDescription && !hasImage) {
    const isRestaurant = request.isRestaurant || false;
    const cacheKey = getCacheKey(textDescription, isRestaurant, userId);
    const cached = getCachedResult(cacheKey, isRestaurant);
    if (cached) {
      return {
        ...cached,
        requiresConfirmation: cached.requiresConfirmation ?? false,
        suspicionReasons: cached.suspicionReasons ?? [],
      };
    }
  }

  if (!hasImage && !textDescription) {
    throw new Error('Either image or text description is required');
  }

  // ── Water shortcut ──
  if (textDescription && !hasImage && isPlainWater(textDescription)) {
    return {
      calories: 0, protein: 0, carbs: 0, fat: 0, fibre: 0,
      hydration_ml: 0,
      needs_amount: true,
      confidence: 'low',
      method: 'estimated',
      notes: 'Volume required for hydration. Please specify amount (e.g., "500ml water").',
      requiresConfirmation: false,
      suspicionReasons: [],
    };
  }

  // ── Determine API kind + timeout ──
  let kind: 'nutrition_text' | 'nutrition_image' | 'nutrition_combined';
  const payload: Record<string, unknown> = {};
  let timeoutMs: number;

  if (textDescription && hasImage) {
    kind = 'nutrition_combined';
    payload.text = textDescription;
    if (imageInput.imageBase64) payload.imageBase64 = imageInput.imageBase64;
    if (imageInput.imageUrl) payload.imageUrl = imageInput.imageUrl;
    timeoutMs = TIMEOUT_IMAGE_MS;
  } else if (hasImage) {
    kind = 'nutrition_image';
    if (imageInput.imageBase64) payload.imageBase64 = imageInput.imageBase64;
    if (imageInput.imageUrl) payload.imageUrl = imageInput.imageUrl;
    timeoutMs = TIMEOUT_IMAGE_MS;
  } else {
    kind = 'nutrition_text';
    payload.text = textDescription;
    timeoutMs = TIMEOUT_TEXT_MS;
  }

  if (userProvidedMacros) payload.userProvidedMacros = userProvidedMacros;
  if (useHighQualityModel) payload.useHighQualityModel = true;
  if (request.isRestaurant) payload.isRestaurant = true;

  // ── API call with unified retry ──
  const inputType = kind.replace('nutrition_', '') as 'text' | 'image' | 'combined';
  let response: Response;

  try {
    response = await fetchWithRetry(
      `${env.apiBaseUrl}/v1/ai/request`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ kind, payload }),
      },
      timeoutMs,
    );
  } catch (err: unknown) {
    const durationMs = Date.now() - startTime;
    const errorMsg = err instanceof Error ? err.message : 'Unknown fetch error';
    emitTelemetry({ event: 'nutrition_parse_result', inputType, confidence: 'unknown', requiresConfirmation: false, suspicionCount: 0, durationMs, error: errorMsg });
    reportAIError(errorMsg).catch(() => {});
    recordFailure();
    throw new Error('Failed to analyze nutrition. Please check your connection and try again.');
  }

  // ── HTTP error handling ──
  if (!response!.ok) {
    const errBody = await response!.json().catch(() => ({ message: 'Unknown error', code: 'unknown' }));

    if (response!.status === 429 && errBody.code === 'daily_ocr_limit') {
      throw new Error("You've reached today's photo limit. You can still log using text.");
    }
    if (response!.status === 402) {
      const retryAfter = errBody.retry_after || 3600;
      throw new BudgetExceededError(retryAfter, errBody.message || 'AI parsing budget exceeded');
    }

    const durationMs = Date.now() - startTime;
    emitTelemetry({ event: 'nutrition_parse_result', inputType, confidence: 'unknown', requiresConfirmation: false, suspicionCount: 0, durationMs, error: `http_${response!.status}` });
    reportAIError(`Backend API error: ${errBody.message || response!.status}`).catch(() => {});

    // Record failure for circuit breaker (only on retryable errors)
    if (RETRYABLE_STATUSES.has(response!.status)) {
      recordFailure();
    }

    throw new Error(`Backend API error: ${errBody.message || JSON.stringify(errBody)}`);
  }

  // ── Parse and validate response ──
  const data = await response!.json();
  const backendResult = data.result;

  if (!backendResult) {
    recordFailure();
    throw new Error('No result from backend API');
  }

  // ── HARD schema validation — throws on failure, no permissive casting ──
  try {
    assertResponseShape(backendResult);
  } catch (shapeErr) {
    recordFailure();
    reportAIError(shapeErr instanceof Error ? shapeErr.message : 'Shape validation failed').catch(() => {});
    throw shapeErr; // Let parseItem catch this and fall back to deterministic
  }

  // ── Normalise totals (with safe coercion) ──
  const coercionReasons: string[] = [];
  const totals = normaliseTotals(backendResult as Record<string, unknown>, coercionReasons);

  // ── Hard discard on coercion failures ──
  // If any field required coercion (was non-numeric), discard the entire result
  if (coercionReasons.length > 0) {
    recordFailure();
    reportAIError(JSON.stringify({ event: 'nutrition_coercion_discard', reasons: coercionReasons })).catch(() => {});
    throw new NutritionShapeError(`Backend returned non-numeric macro fields: ${coercionReasons.join('; ')}`);
  }

  // ── All-zero validation ──
  const allZero =
    totals.calories === 0 && totals.protein === 0 &&
    totals.carbs === 0 && totals.fat === 0 && totals.fibre === 0;

  const isSupplement =
    (backendResult as Record<string, unknown>).notes?.toString().toLowerCase().includes('supplement') ||
    (backendResult as Record<string, unknown>).notes?.toString().toLowerCase().includes('creatine') ||
    totals.creatine > 0;

  // Check needs_amount at both top-level AND item-level.
  // The BE sanitiser sets needs_amount on individual items but not on the top-level result.
  // When all items have needs_amount=true, totals are all-zero (nulled by sanitiser).
  // This is NOT a parse failure — the caller (parseItem) may already have the weight.
  const topLevelNeedsAmount = !!(backendResult as Record<string, unknown>).needs_amount;
  const itemsNeedAmount = Array.isArray((backendResult as Record<string, unknown>).items) &&
    ((backendResult as Record<string, unknown>).items as any[]).some((item: any) => item?.needs_amount);
  const needsAmount = topLevelNeedsAmount || itemsNeedAmount;

  const needsManualMacros = !!(backendResult as Record<string, unknown>).needs_manual_macros;

  if (allZero && !isSupplement && !needsAmount && !needsManualMacros) {
    const isPhoto = hasImage;
    throw new Error(
      isPhoto
        ? "We couldn't read this label. Please try text entry instead — type the food name and macros from the label."
        : "Couldn't parse this item. Please check the spelling or add a quantity.",
    );
  }

  // ── Plausibility + reconciliation ──
  const plausibility = checkPlausibility(
    totals.calories, totals.protein, totals.carbs, totals.fat, totals.fibre,
  );

  const backendConfidence = ((backendResult as Record<string, unknown>).confidence as string) || 'medium';
  if (backendConfidence === 'low') {
    plausibility.requiresConfirmation = true;
    if (!plausibility.suspicionReasons.some((r) => r.includes('confidence'))) {
      plausibility.suspicionReasons.push('Low confidence — the values may not be accurate');
    }
  }

  const usesPerPortion = !!(backendResult as Record<string, unknown>).uses_per_portion;
  if (usesPerPortion && totals.calories > 1500) {
    plausibility.requiresConfirmation = true;
    plausibility.suspicionReasons.push('Per-portion calories seem very high — please verify serving size');
  }

  // ── Build result ──
  const extraction: NutritionExtraction = {
    calories: totals.calories,
    protein: totals.protein,
    carbs: totals.carbs,
    fat: totals.fat,
    fibre: totals.fibre,
    creatine: totals.creatine,
    hydration_ml: totals.hydration_ml,
    needs_amount: !!(backendResult as Record<string, unknown>).needs_amount,
    needs_manual_macros: !!(backendResult as Record<string, unknown>).needs_manual_macros,
    confidence: backendConfidence as 'high' | 'medium' | 'low',
    method: ((backendResult as Record<string, unknown>).method as 'extracted' | 'estimated') || 'estimated',
    uses_per_portion: usesPerPortion,
    notes: ((backendResult as Record<string, unknown>).notes as string) || undefined,
    requiresConfirmation: plausibility.requiresConfirmation,
    suspicionReasons: plausibility.suspicionReasons,
  };

  // ── Extract label weight metadata from first item (label uploads only, single-item) ──
  const isLabelExtraction = extraction.method === 'extracted';
  const items = Array.isArray((backendResult as Record<string, unknown>).items)
    ? ((backendResult as Record<string, unknown>).items as any[])
    : [];
  if (isLabelExtraction && items.length >= 1) {
    const first = items[0];
    if (typeof first?.serving_size_grams === 'number') extraction.serving_size_grams = first.serving_size_grams;
    if (typeof first?.net_weight_grams === 'number') extraction.net_weight_grams = first.net_weight_grams;
    if (typeof first?.servings_per_container === 'number') extraction.servings_per_container = first.servings_per_container;
    if (typeof first?.volume_ml === 'number') extraction.label_volume_ml = first.volume_ml;
  }

  // ── Attach raw items for ConfirmationSheet when confirmation is required ──
  if (extraction.requiresConfirmation && Array.isArray((backendResult as Record<string, unknown>).items)) {
    extraction.confirmationItems = ((backendResult as Record<string, unknown>).items as any[]).map((item: any) => ({
      name: typeof item.name === 'string' ? item.name : 'Unknown item',
      quantity: typeof item.quantity === 'number' ? item.quantity : null,
      unit: typeof item.unit === 'string' ? item.unit : null,
      calories: typeof item.calories === 'number' ? item.calories : null,
      protein: typeof item.protein === 'number' ? item.protein : null,
      carbs: typeof item.carbs === 'number' ? item.carbs : null,
      fat: typeof item.fat === 'number' ? item.fat : null,
      fibre: typeof item.fibre === 'number' ? item.fibre : null,
      hydration_ml: typeof item.hydration_ml === 'number' ? item.hydration_ml : null,
      creatine: typeof item.creatine === 'number' ? item.creatine : null,
      needs_amount: !!item.needs_amount,
      missing_fields: Array.isArray(item.missing_fields) ? item.missing_fields : null,
      confidence: typeof item.confidence === 'string' ? item.confidence : (backendConfidence || undefined),
      notes: typeof item.notes === 'string' ? item.notes : undefined,
    }));
  }

  // ── Telemetry ──
  const durationMs = Date.now() - startTime;
  emitTelemetry({
    event: 'nutrition_parse_result',
    inputType,
    confidence: extraction.confidence,
    requiresConfirmation: extraction.requiresConfirmation,
    suspicionCount: extraction.suspicionReasons.length,
    durationMs,
  });

  // ── Record success for circuit breaker ──
  recordSuccess();

  // ── Cache (text-only) ──
  if (textDescription && !hasImage && userId) {
    const isRestaurant = request.isRestaurant || false;
    const cacheKey = getCacheKey(textDescription, isRestaurant, userId);
    setCachedResult(cacheKey, extraction);
  }

  return extraction;
}

// ────────────────────────────────────────────────────────────────
// Item extraction — thin wrapper around regex splitting.
// FE HARDENING: removed dead "strict retry" path (regex produces
// identical results on retry). Callers can use splitMealItems directly.
// ────────────────────────────────────────────────────────────────

export async function extractItems(
  textDescription: string,
  _userId: string,
  _retryStrict: boolean = false,
): Promise<ItemExtraction> {
  const { splitMealItems } = await import('../../utils/multiItemParser');
  return { items: splitMealItems(textDescription) };
}

// ────────────────────────────────────────────────────────────────
// Confirmed nutrition submission — zero AI, deterministic only
// ────────────────────────────────────────────────────────────────

export interface ConfirmedNutritionRequest {
  userId: string;
  items: Array<{
    name: string;
    quantity: number;
    unit: string;
    cookedDry?: 'cooked' | 'dry';
    /** Pre-parsed macros from original AI response (passed through for non-staples items) */
    calories?: number | null;
    protein?: number | null;
    carbs?: number | null;
    fat?: number | null;
    fibre?: number | null;
    hydration_ml?: number | null;
    creatine?: number | null;
  }>;
  requestId?: string;
}

/**
 * Submit user-confirmed nutrition items to the backend.
 *
 * CRITICAL: This does NOT call AI. It sends structured items to
 * `nutrition_confirmed` which resolves deterministically via staples
 * or accepts the pre-parsed macros. Cannot loop back into confirmation.
 */
export async function submitConfirmedNutrition(
  request: ConfirmedNutritionRequest,
): Promise<NutritionExtraction> {
  let authToken: string;
  try {
    const { getFirebaseIdToken } = await import('../../utils/firebaseToken');
    authToken = await getFirebaseIdToken();
  } catch {
    throw new Error('Authentication required');
  }

  const response = await loggedFetch(
    `${env.apiBaseUrl}/v1/ai/request`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        kind: 'nutrition_confirmed',
        payload: {
          items: request.items,
          requestId: request.requestId,
        },
      }),
    },
  );

  if (!response.ok) {
    const errBody = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(`Confirmation failed: ${errBody.message || response.status}`);
  }

  const data = await response.json();
  const result = data.result;

  if (!result || !Array.isArray(result.items) || result.items.length === 0) {
    throw new Error(result?.errors?.join('; ') || 'No items could be resolved from confirmation.');
  }

  // Build NutritionExtraction from the confirmed result
  const total = result.total || {};
  const confirmed: NutritionExtraction = {
    calories: total.calories ?? 0,
    protein: total.protein ?? 0,
    carbs: total.carbs ?? 0,
    fat: total.fat ?? 0,
    fibre: total.fibre ?? 0,
    creatine: total.creatine ?? 0,
    hydration_ml: total.hydration_ml ?? 0,
    confidence: result.confidence || 'high',
    method: 'estimated',
    // CRITICAL: Never allow confirmation to loop. Force false regardless of backend response.
    requiresConfirmation: false,
    suspicionReasons: [],
  };

  return confirmed;
}

// ────────────────────────────────────────────────────────────────
// Image utilities
// ────────────────────────────────────────────────────────────────

export function imageToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
